import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';

import { getCoach, getUser } from '@/lib/api-calls';
import { airServerErrorFrom, axiosErrorFrom } from '@/lib/error-handling';
import { QueryKeys } from '@/lib/queries';

import { InfoPage } from '@/components/info-page';
import { InfoPageSuspenseBoundary } from '@/components/info-page-suspense-boundary';

import { BasicInfoCard } from '../-components/basic-info-card';
import { ReadingSetupCard } from '../-components/reading-setup-card';
import { ManagePermissionsDropdown } from '../-components/manage-permissions-dropdown';
import { TeachingSectionCard } from '../-components/teaching-section-card';
import { coachesTabs } from '../-components/tabs';
import { validateRouteRole } from '@/lib/routing';
import { isAirAdmin } from '@airreading/common';

export const Route = createFileRoute('/_layout/teachers/$teacherId/')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: CoachPage,
});

function CoachPage() {
  return (
    <InfoPageSuspenseBoundary
      onError={(e) => {
        const axiosError = axiosErrorFrom(e);
        const airServerError = airServerErrorFrom(e);

        return {
          allowRetry: axiosError?.response?.status !== 404,
          errorHeader: airServerError?.userFacingMessage || 'Failed to load teacher. Please try again.',
        };
      }}
    >
      <CoachDetail />
    </InfoPageSuspenseBoundary>
  );
}

function CoachDetail() {
  const { teacherId: userId } = Route.useParams();
  const { data: user } = useSuspenseQuery({
    queryKey: QueryKeys.getUser(userId),
    queryFn: () => getUser(userId),
  });
  const { data: coach } = useSuspenseQuery(getCoach(userId));

  return (
    <div className="mb-2">
      <InfoPage
        headerText={user.fullNameOrEmail}
        headerActions={<ManagePermissionsDropdown user={user} />}
        tabs={coachesTabs({ teacherId: userId })}
        breadcrumbs={{
          linkItems: [
            {
              label: 'Teachers',
              link: {
                to: '/teachers',
              },
            },
          ],
          currentPage: user.fullNameOrEmail,
        }}
      >
        <>
          <BasicInfoCard coach={coach} userId={userId} />
          <ReadingSetupCard coach={coach} userId={userId} />
          <TeachingSectionCard coach={coach} userId={userId} />
        </>
      </InfoPage>
    </div>
  );
}
